// `usePresenceRoster` — the authoritative online roster for one auction
// (BL-410 AC 4).
//
// Returns the `customer_user.id` of everyone currently connected. Identity is
// the USER — paddle number has no part in presence and must not be joined into
// this hook's return (BL-410, 2026-09-02). A caller that renders a paddle joins
// it from its own authenticated data, keyed on the user id.
//
// The store is memoized per subscriber rather than per component, so every
// consumer of the same auction shares one subscription (the rail and its header
// count are two consumers of one roster).

'use client';

import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { PresenceStore } from '../../stores/realtime/presence-store';
import { useRealtimeSubscriber } from '../../components/realtime/RealtimeProvider';
import type { RealtimeSubscriber } from '../../types/realtime/subscriber';

/**
 * One store per subscriber.
 *
 * A WeakMap rather than a module-level singleton: tests construct a fresh
 * subscriber per case and would otherwise inherit the previous case's rosters,
 * and a keyed-on-subscriber store is garbage-collected with it.
 */
const storesBySubscriber = new WeakMap<RealtimeSubscriber, PresenceStore>();

/** Get (or create) the store bound to this subscriber. */
function storeFor(subscriber: RealtimeSubscriber): PresenceStore {
    const existing = storesBySubscriber.get(subscriber);
    if (existing) return existing;
    const created = new PresenceStore(subscriber);
    storesBySubscriber.set(subscriber, created);
    return created;
}

/**
 * Subscribe to an auction's online roster.
 *
 * @param topic - The presence topic (`topics.auction(id).presence()`). Pass an
 *   empty string to disable — no subscription is taken, and the hook still runs
 *   unconditionally, so callers need no conditional hook call (`ui-standards.md`
 *   rule #7).
 * @returns The set of online `customer_user.id`s. Empty before the first frame,
 *   never `undefined` — a rail rendering on first paint gets a usable value
 *   rather than a loading flicker.
 * @sideEffect Holds a shared realtime subscription for the topic while mounted.
 */
export function usePresenceRoster(topic: string): ReadonlySet<string> {
    const subscriber = useRealtimeSubscriber();
    const store = useMemo(() => storeFor(subscriber), [subscriber]);

    const subscribe = useCallback(
        (onChange: () => void) => store.subscribe(topic, onChange),
        [store, topic],
    );
    const getSnapshot = useCallback(() => store.get(topic), [store, topic]);

    // Server render has no socket and therefore no roster — the same empty set
    // the client starts from, so hydration matches.
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
